import { z } from "zod";

/**
 * Devices and push notifications: `staff.devices.*`. Web Push for staff web
 * (P3-T32); the Android app's FCM registration token (P6-T02, sent to FCM
 * directly, ADR-027). See docs/architecture/realtime-notifications.md, Push rules.
 */

/**
 * Push services a browser may hand out. The worker posts to the endpoint,
 * so anything else is refused: an endpoint must never point the worker at
 * an internal address (security.md).
 */
export const WEB_PUSH_HOSTS = [
	"fcm.googleapis.com",
	"push.services.mozilla.com",
	"notify.windows.com",
	"push.apple.com",
] as const;

/** `https://<host>/<path>`: no port, no user, no IP address tricks (a host of known names only). */
const ENDPOINT = /^https:\/\/([a-z0-9-]+(?:\.[a-z0-9-]+)+)\/[^\s#]*$/i;

export function isWebPushEndpoint(endpoint: string): boolean {
	const host = ENDPOINT.exec(endpoint)?.[1]?.toLowerCase();
	if (!host) return false;
	return WEB_PUSH_HOSTS.some((known) => host === known || host.endsWith(`.${known}`));
}

/** A browser's `PushSubscription.toJSON()`, the parts the server needs. */
export const webPushSubscriptionSchema = z.object({
	endpoint: z.string().max(1000).refine(isWebPushEndpoint, "Not a known push service"),
	keys: z.object({
		p256dh: z.string().min(1).max(200),
		auth: z.string().min(1).max(100),
	}),
});
export type WebPushSubscription = z.infer<typeof webPushSubscriptionSchema>;

/**
 * An FCM registration token of the Android app (`getDevicePushTokenAsync`).
 * Opaque, about 160 characters; the worker sends it only to FCM.
 */
export const fcmTokenSchema = z
	.string()
	.min(20)
	.max(4096)
	.regex(/^[A-Za-z0-9:_\-.]+$/, "Not an FCM token");

/**
 * `staff.devices.register`: this browser (or phone) gets pushes for the
 * active restaurant. `id` is made once by the device and kept; calling
 * again updates the same device. Staff web sends `webPush`; the Android app
 * registers at sign-in with `platform: "android"` and its `fcmToken` (null
 * when the build has no Firebase credentials or the person refused
 * notifications: the device is still known, with push off).
 */
export const registerDeviceInputSchema = z
	.object({
		id: z.uuid(),
		name: z.string().trim().max(80).nullable().optional(),
		platform: z.enum(["android", "web"]).default("web"),
		webPush: webPushSubscriptionSchema.optional(),
		fcmToken: fcmTokenSchema.nullable().optional(),
	})
	.refine((d) => d.platform === "web" || d.webPush === undefined, {
		message: "Web push is for browsers",
		path: ["webPush"],
	})
	.refine((d) => d.platform === "android" || d.webPush !== undefined, {
		message: "A browser registers with its push subscription",
		path: ["webPush"],
	})
	.refine((d) => d.platform === "android" || d.fcmToken == null, {
		message: "FCM tokens are for the Android app",
		path: ["fcmToken"],
	});
export type RegisterDeviceInput = z.input<typeof registerDeviceInputSchema>;

export const unregisterDeviceInputSchema = z.object({ id: z.uuid() });

/**
 * What a device is told about (realtime-notifications.md, Push rules): each
 * kind can be switched off on the device. Orders are diners' orders waiting
 * for a decision, requests are calls from tables, payments are diners'
 * "I have paid".
 */
export const PUSH_KINDS = ["orders", "requests", "payments"] as const;
export type PushKind = (typeof PUSH_KINDS)[number];

export const devicePrefsSchema = z.object({
	orders: z.boolean(),
	requests: z.boolean(),
	payments: z.boolean(),
});
export type DevicePrefs = z.infer<typeof devicePrefsSchema>;

export const deviceSchema = z.object({
	id: z.uuid(),
	platform: z.enum(["android", "web"]),
	restaurantId: z.uuid().nullable(),
	/** The server holds a push subscription or token for it. */
	pushEnabled: z.boolean(),
	/** Off duty: no pushes at all, without signing out (P6-T06). On at sign-in. */
	onDuty: z.boolean(),
	prefs: devicePrefsSchema,
});
export type Device = z.infer<typeof deviceSchema>;

/** `staff.devices.get`: the caller's device, or null when the server does not know it. */
export const getDeviceInputSchema = z.object({ id: z.uuid() });

/** `staff.devices.update`: the on-duty switch and the preferences of the caller's device. */
export const updateDeviceInputSchema = z.object({
	id: z.uuid(),
	onDuty: z.boolean().optional(),
	prefs: devicePrefsSchema.partial().optional(),
});
export type UpdateDeviceInput = z.input<typeof updateDeviceInputSchema>;

/**
 * `staff.devices.getPushConfig`: `webPushPublicKey` is null when the server
 * sends no web push; `androidPush` is false when it has no FCM credentials.
 */
export const pushConfigSchema = z.object({
	webPushPublicKey: z.string().nullable(),
	androidPush: z.boolean(),
});
export type PushConfig = z.infer<typeof pushConfigSchema>;

/** What a push carries to the service worker: already in the staff member's words. */
export const pushPayloadSchema = z.object({
	title: z.string(),
	body: z.string(),
	/** One notification per batch or request: a newer push with the same tag replaces it. */
	tag: z.string(),
	/** Opened on a tap. */
	url: z.string(),
});
export type PushPayload = z.infer<typeof pushPayloadSchema>;

/**
 * Android notification channels (P6-T03). The app creates them at start; a
 * push names one. New orders and calls have the loudest settings and their
 * own sounds; `counter` is counter mode's quiet persistent notification;
 * `daily-summary` is the owner's daily summary (P11-T08), quiet too.
 */
export const ANDROID_CHANNELS = {
	newOrders: "new-orders",
	serviceRequests: "service-requests",
	payments: "payments",
	general: "general",
	counter: "counter",
	dailySummary: "daily-summary",
} as const;

/** The category of a new order notification: its Accept and Reject buttons (P6-T07). */
export const NEW_ORDER_CATEGORY = "new-order";

/**
 * What the Android app does with a push (`kind` in its data):
 * `newOrder` (a QR batch waiting, with `batchId`), `serviceRequest`
 * (`requestId`), `paymentClaim` (`orderId`), `notice` (owners' notices),
 * and `dismiss` (silent: someone decided it, so the notification with `tag`
 * goes away on every phone).
 */
export const ANDROID_PUSH_KINDS = [
	"newOrder",
	"serviceRequest",
	"paymentClaim",
	"notice",
	"dismiss",
] as const;
export type AndroidPushKind = (typeof ANDROID_PUSH_KINDS)[number];

/**
 * The app-defined part of an FCM data message to the Android app (the
 * expo-notifications keys `title`, `message`, `channelId`, `categoryId` and
 * `tag` sit beside it; see the push module's FCM sender). All strings: FCM
 * data values are strings.
 */
export const androidPushDataSchema = z.object({
	kind: z.enum(ANDROID_PUSH_KINDS),
	restaurantId: z.uuid(),
	/** The app route a tap opens, such as `/orders`. */
	url: z.string().startsWith("/"),
	tag: z.string(),
	batchId: z.uuid().optional(),
	requestId: z.uuid().optional(),
	orderId: z.uuid().optional(),
});
export type AndroidPushData = z.infer<typeof androidPushDataSchema>;
